/**
 * Thin wrappers over {@link logError} for the two error shapes that were being
 * hand-rolled at every call site (and, more often, skipped entirely — see the
 * telemetry gap audit). Centralising them means one PII-safe extraction of the
 * error name + message and one consistent property bag, so a new failure site is
 * a single call instead of a five-line block that is easy to omit.
 *
 * `error.office-js` — a runtime Office host-API failure (Word.run / PowerPoint.run
 * / Office.context.* / Outlook item.*). Previously emitted only for the
 * synchronous boot throw in main.tsx; every runtime host-action failure was
 * console-only.
 *
 * `error.connector.failure` — a connector action that failed for a reason the
 * auto HTTP telemetry (error.api.* / error.network) does not capture: a non-HTTP
 * throw, a transport that bypasses fetchWithRefresh (SignalR), or a
 * business-logic failure after a 2xx.
 */
import { logError, logEvent } from "./provider";
import { scrubMessage } from "./scrub";
import type { TelemetryProperties } from "./types";

/** Extract a PII-safe {type, scrubbedMessage} block from any thrown value. */
function toErrorBlock(err: unknown, fallbackType: string) {
  return {
    type: err instanceof Error ? err.name : fallbackType,
    scrubbedMessage: scrubMessage(err instanceof Error ? err.message : String(err)),
  };
}

/**
 * Log a runtime Office host-API failure as `error.office-js`. Pass `reason` to
 * name the action that failed (e.g. the office-action handler id or a host op),
 * so failures can be sliced without leaking document content.
 */
export function logOfficeError(err: unknown, reason?: string): void {
  logError(
    "error.office-js",
    toErrorBlock(err, "OfficeJsError"),
    reason ? { reason } : undefined,
  );
}

/**
 * Log a connector action failure as `error.connector.failure`, tagged with the
 * connector's systemName so it lands in the connector pivot. `reason` names the
 * specific operation; extra allow-listed props may be merged via `properties`.
 */
export function logConnectorFailure(
  connectorSystemName: string,
  err: unknown,
  reason?: string,
  properties?: TelemetryProperties,
): void {
  logError(
    "error.connector.failure",
    toErrorBlock(err, "ConnectorError"),
    { connectorSystemName, ...(reason ? { reason } : {}), ...properties },
  );
}

/**
 * Log a connector action that SUCCEEDED, as `connector.action.invoke` at
 * `info`, tagged with the same `connectorSystemName` its failure sibling uses.
 *
 * AIVA-6494. Pair this with every {@link logConnectorFailure} call. Without it
 * `connectorSystemName` reaches telemetry only on the failure path, so AiVA
 * Central's Connector reliability card divides failures by failures and reports
 * <strong>0.0% for every connector</strong> — which is indistinguishable from a
 * genuinely dead integration. Measured over 24h in the dev fleet:
 * `notifications`, `myqueue`, `docuparser` and both `engage-*` connectors all
 * read 0.0%, while `deckorate` — the only one already emitting
 * `connector.action.invoke` — read a plausible 80.0%. The metric is undefined
 * without a success event, not zero.
 *
 * `info` matters: the card counts a row as failed when `eventKind` is `error`,
 * `statusCode >= 400`, `errorCode` is set, or `stage` is `tool_error`. An
 * `info` row carrying none of those is what makes the denominator whole.
 *
 * Use the same `reason` stem as the paired failure call (`search` /
 * `search-failed`) so a connector's operations can be sliced without joining on
 * event name.
 */
export function logConnectorSuccess(
  connectorSystemName: string,
  reason?: string,
  properties?: TelemetryProperties,
): void {
  logEvent(
    "connector.action.invoke",
    "info",
    { connectorSystemName, ...(reason ? { reason } : {}), ...properties },
  );
}
